import { Check, Edit, MoreHoriz } from '@mui/icons-material';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { ActionButtonLink } from '~/components/button';
import { ThumbnailContainer } from '~/pages/channel-guides/lib';
import { formatEncryption } from '~/pages/channels/lib/formatters';
import type { ChannelList__Channel as Channel } from '../../queries/list.generated';
import { Menu } from '../components/menu';

export const useColumns = (
  onAddToGuideClick: (channel: Channel) => void,
  enableLiveChannelStreaming: boolean,
): GridColDef<Channel>[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(
    () => [
      {
        field: 'thumbnailFile',
        headerName: t('common.labels.thumbnail'),
        minWidth: 100,
        renderCell: ({ row }) => (
          <Tooltip title={`Channel ID: ${row.id}`}>
            <ThumbnailContainer sx={{ backgroundColor: row.backgroundColor, height: '75%' }}>
              <img
                alt={row.name}
                crossOrigin="anonymous"
                src={row.thumbnailFile.uri}
                style={{ margin: '0.5rem', height: '100%' }}
              />
            </ThumbnailContainer>
          </Tooltip>
        ),
        sortable: false,
      },
      {
        field: 'number',
        headerName: t('pages.channels.list.columns.number'),
        resizable: false,
        sortable: true,
        minWidth: 100,
      },
      {
        field: 'name',
        headerName: t('common.labels.name'),
        flex: 1,
        minWidth: isSmallAndUp ? 180 : 0,
        resizable: true,
        sortable: true,
      },
      {
        field: 'url',
        minWidth: 250,
        resizable: true,
        sortable: true,
        headerName: t('pages.channels.list.columns.url'),
      },
      {
        field: 'outputUrl',
        minWidth: 250,
        resizable: true,
        sortable: true,
        headerName: t('pages.channels.list.columns.output_url'),
        renderCell: ({ row }) => {
          // Don't display output URL when encryption is NONE
          if (row.encryption === 'NONE') {
            return '--';
          }
          return row.outputUrl || '--';
        },
      },
      {
        field: 'encoding',
        headerName: t('pages.channels.list.columns.encoding'),
        minWidth: 50,
        resizable: true,
        sortable: true,
        renderCell: ({ row }) => row.metadata?.videoStream?.codecName ?? '--',
      },
      {
        field: 'encryption',
        headerName: t('pages.channels.list.columns.encryption'),
        resizable: false,
        sortable: false,
        width: 110,
        valueFormatter: (row) => formatEncryption(row),
      },
      {
        field: 'source',
        headerName: t('common.labels.source'),
        minWidth: 125,
        resizable: false,
        sortable: true,
      },
      ...(enableLiveChannelStreaming
        ? [
            {
              align: 'center' as const,
              field: 'enabledForLiveStreaming',
              headerName: t('pages.channels.list.columns.live_streaming'),
              minWidth: 120,
              resizable: false,
              sortable: true,
              renderCell: ({ row }: { row: Channel }) =>
                row.enabledForLiveStreaming ? <Check color="success" /> : null,
            },
          ]
        : []),
      {
        align: 'center',
        field: 'channelGuides',
        headerName: t('pages.channels.list.columns.channel_guides'),
        minWidth: 125,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return <Chip color="primary" label={row.channelGuideCount} />;
        },
      },
      {
        field: 'actions',
        headerName: t('pages.channels.list.columns.actions'),
        type: 'actions',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }) => {
          return [
            <ActionButtonLink
              key={`edit-${row.id}`}
              title={t('common.actions.edit')}
              Icon={Edit}
              to={`${row.id}`}
            />,
            <Menu
              buttonType="action"
              channel={row}
              Icon={MoreHoriz}
              key={`more-action-${row.id}`}
              onAddToGuideClick={onAddToGuideClick}
            />,
          ];
        },
      },
    ],
    [enableLiveChannelStreaming, isSmallAndUp, onAddToGuideClick, t],
  );
};
